'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

interface NavItem {
  label: string;
  href: string;
}

interface HeaderMenuProps {
  navItems: NavItem[];
  defaultRoute: string;
}

export default function HeaderMenu({
  navItems,
  defaultRoute,
}: HeaderMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  let pathname = usePathname() || defaultRoute;

  if (pathname === '/') pathname = defaultRoute;

  const closeMenu = () => setIsOpen(false);

  return (
    <motion.nav className="relative lg:hidden" aria-label="Mobile navigation">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle menu"
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <XMarkIcon className="h-8 w-8 text-primary" />
        ) : (
          <Bars3Icon className="h-8 w-8 text-primary" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-12 bg-base-200 shadow-lg rounded-lg p-3 w-60"
          >
            <ul className="menu menu-compact">
              {navItems.map(({ label, href }) => (
                <motion.li key={href}>
                  <Link
                    href={href}
                    className={`block px-4 py-2 w-full rounded-lg text-base-content ${
                      pathname === href
                        ? 'bg-base-100 text-neutral-content font-bold'
                        : 'hover:bg-neutral'
                    }`}
                    aria-current={pathname === href ? 'page' : undefined}
                    onClick={closeMenu}
                  >
                    {label}
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
